import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Disc3, MapPinned, Radio, Sparkles, UserRound, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { API_BASE as API } from '../lib/apiBase';


const genreOptions = ['indie', 'pop', 'electronic', 'latin', 'urban', 'rock', 'afro', 'ambient'];
const moodOptions = ['noche', 'descubrir', 'entrenar', 'viajar', 'enfoque', 'romance'];


const legalConfig = {
  terms: { endpoint: 'terms', labelEs: 'Términos y Condiciones', labelEn: 'Terms and Conditions' },
  privacy: { endpoint: 'privacy', labelEs: 'Política de Privacidad', labelEn: 'Privacy Policy' },
  copyright: { endpoint: 'copyright-policy', labelEs: 'Copyright y Moderación', labelEn: 'Copyright & Moderation' },
  artist: { endpoint: 'documents/artist_onboarding_terms', labelEs: 'Condiciones de artista', labelEn: 'Artist terms' },
};


export default function OnboardingPage() {
  const { token, user, updateUser, refreshUser } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preparing, setPreparing] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, title: '', content: null, loading: false });
  const [form, setForm] = useState({
    selected_role: user?.identity_mode || user?.role || 'user',
    name: user?.name || '',
    favorite_genres: [],
    favorite_moods: [],
    artist_name: '',
    artist_genres: [],
    artist_story: '',
    city: '',
    signer_name: user?.name || '',
    accept_terms: false,
    accept_privacy: false,
    accept_artist_terms: false,
    tax_country: '',
    tax_id: '',
    tax_type: 'individual',
    vat_registered: false,
  });
  const [taxRates, setTaxRates] = useState({});

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  useEffect(() => {
    fetch(`${API}/api/fiscal/tax-rates`)
      .then((r) => r.json())
      .then((d) => setTaxRates(d.tax_rates || {}))
      .catch(() => {});
  }, []);

  const onboardingSummary = useMemo(() => ({
    intent: form.selected_role === 'artist' ? 'artist' : 'discover',
    genres: form.selected_role === 'artist' ? form.artist_genres : form.favorite_genres,
    moods: form.favorite_moods,
  }), [form]);

  const toggleItem = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: current[key].includes(value) ? current[key].filter((item) => item !== value) : [...current[key], value],
    }));
  };

  const openLegalModal = async (key) => {
    const config = legalConfig[key];
    if (!config) return;
    setLegalModal({ open: true, title: lang === 'es' ? config.labelEs : config.labelEn, content: null, loading: true });
    try {
      const res = await fetch(`${API}/api/legal/${config.endpoint}?lang=${lang}`);
      const data = await res.json();
      setLegalModal({ open: true, title: data.title || (lang === 'es' ? config.labelEs : config.labelEn), content: data, loading: false });
    } catch (e) {
      setLegalModal({ open: true, title: lang === 'es' ? config.labelEs : config.labelEn, content: null, loading: false });
    }
  };

  const validateStep = () => {
    if (step === 1 && form.selected_role === 'user' && form.favorite_genres.length === 0) {
      setError(lang === 'es' ? 'Elige al menos un género para preparar tu feed.' : 'Choose at least one genre to prepare your feed.');
      return false;
    }
    if (step === 1 && form.selected_role === 'artist' && !form.artist_name.trim()) {
      setError(lang === 'es' ? 'Cuéntanos al menos tu nombre artístico.' : 'Tell us at least your artist name.');
      return false;
    }
    if (step === 1 && form.selected_role === 'artist' && !form.tax_country) {
      setError(lang === 'es' ? 'Selecciona tu país de residencia fiscal.' : 'Select your tax residence country.');
      return false;
    }
    if (step === 3 && (!form.accept_terms || !form.accept_privacy || (form.selected_role === 'artist' && !form.accept_artist_terms))) {
      setError(lang === 'es' ? 'Necesitas aceptar los documentos para continuar.' : 'You need to accept the required documents to continue.');
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStep((current) => Math.min(3, current + 1));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setPreparing(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/users/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo completar el onboarding');
      updateUser(data.user);
      await refreshUser();
      localStorage.setItem('pulse_onboarding_summary', JSON.stringify(onboardingSummary));
      localStorage.setItem('pulse_show_guided_tips', '1');
      setTimeout(() => {
        navigate(form.selected_role === 'artist' ? '/?welcome=artist' : '/?welcome=prepared', { replace: true });
      }, 900);
    } catch (e) {
      setError(e.message || 'Error');
      setPreparing(false);
      setLoading(false);
    }
  };

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-[#050505] text-white px-4 pb-16 pt-24">
      {preparing && (
        <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-2xl flex items-center justify-center px-4">
          <div className="max-w-md w-full rounded-[32px] border border-white/10 bg-black/80 p-8 text-center">
            <div className="w-14 h-14 rounded-full border-2 border-pulse-primary border-t-transparent animate-spin mx-auto mb-5" />
            <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-3">PulseMusic</p>
            <h2 className="font-heading text-3xl font-bold mb-3">{lang === 'es' ? 'Preparando tu experiencia...' : 'Preparing your experience...'}</h2>
            <div className="flex flex-wrap justify-center gap-2 mt-5">
              <span className="rounded-full border border-white/10 px-3 py-1 text-xs">{onboardingSummary.intent === 'artist' ? '🎵 Artist' : '🎧 Discover'}</span>
              {onboardingSummary.genres.slice(0, 3).map((item) => <span key={item} className="rounded-full border border-white/10 px-3 py-1 text-xs">{item}</span>)}
              {onboardingSummary.moods.slice(0, 2).map((item) => <span key={item} className="rounded-full border border-white/10 px-3 py-1 text-xs">{item}</span>)}
            </div>
          </div>
        </div>
      )}

      {legalModal.open && (
        <div className="fixed inset-0 z-[85] bg-black/80 backdrop-blur-xl flex items-center justify-center px-4">
          <div className="max-w-3xl w-full max-h-[82vh] overflow-hidden rounded-[30px] border border-white/10 bg-[#0b0b0b]">
            <div className="flex items-center justify-between gap-4 border-b border-white/5 px-5 py-4">
              <h3 className="font-heading font-bold text-xl">{legalModal.title}</h3>
              <button data-testid="close-legal-modal" onClick={() => setLegalModal({ open: false, title: '', content: null, loading: false })} className="p-2 rounded-full border border-white/10"><X size={16} /></button>
            </div>
            <div className="overflow-y-auto max-h-[70vh] px-5 py-5 space-y-5">
              {legalModal.loading ? <div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" /> : legalModal.content?.sections ? legalModal.content.sections.map((section, index) => (
                <div key={`${section.heading}-${index}`}>
                  <h4 className="font-heading text-lg font-semibold mb-2">{section.heading}</h4>
                  <p className="text-sm text-white/75 leading-relaxed">{section.content}</p>
                </div>
              )) : <p className="text-sm text-white/70">{legalModal.content?.content || (lang === 'es' ? 'No se pudo cargar el documento.' : 'Could not load the document.')}</p>}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto">
        <div className="rounded-[34px] border border-white/10 bg-black/60 backdrop-blur-xl overflow-hidden">
          <div className="p-6 md:p-10 border-b border-white/5">
            <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-4">PulseMusic</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-none">
              {lang === 'es' ? 'Cómo quieres vivir PulseMusic' : 'How do you want to live PulseMusic'}
            </h1>
            <p className="mt-4 text-sm md:text-base text-white/75 max-w-2xl leading-relaxed">
              {lang === 'es'
                ? 'Tu entrada no será decorativa: lo que elijas aquí va a mover tu experiencia desde el primer minuto.'
                : 'This is not decorative onboarding: what you choose here will shape your experience from minute one.'}
            </p>
          </div>

          <div className="p-6 md:p-10 space-y-8">
            <div className="flex items-center gap-2 text-xs text-pulse-muted uppercase tracking-[0.2em]">
              {[0, 1, 2, 3].map((item) => (
                <div key={item} className={`h-1.5 flex-1 rounded-full ${step >= item ? 'bg-pulse-primary' : 'bg-white/10'}`} />
              ))}
            </div>

            {step === 0 && (
              <div className="space-y-4">
                <h2 className="font-heading text-2xl font-bold">{lang === 'es' ? 'Elige tu intención' : 'Choose your intention'}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <button data-testid="onboarding-role-user" onClick={() => setForm((f) => ({ ...f, selected_role: 'user' }))} className={`rounded-[28px] border p-6 text-left transition-transform duration-300 ${form.selected_role === 'user' ? 'border-cyan-400 bg-cyan-400/10 scale-[1.01]' : 'border-white/10 bg-white/[0.03]'}`}>
                    <UserRound size={22} className="text-cyan-300 mb-4" />
                    <p className="font-heading text-xl font-semibold mb-2">{lang === 'es' ? 'Vengo a descubrir' : 'I am here to discover'}</p>
                    <p className="text-sm text-white/70">{lang === 'es' ? 'Entrar en una experiencia hecha para mis gustos, seguir artistas y moverme por la escena.' : 'Step into a feed shaped by my taste, follow artists and move through the scene.'}</p>
                  </button>
                  <button data-testid="onboarding-role-artist" onClick={() => setForm((f) => ({ ...f, selected_role: 'artist' }))} className={`rounded-[28px] border p-6 text-left transition-transform duration-300 ${form.selected_role === 'artist' ? 'border-[#FF3B30] bg-[#FF3B30]/10 scale-[1.01]' : 'border-white/10 bg-white/[0.03]'}`}>
                    <Disc3 size={22} className="text-[#FF6B60] mb-4" />
                    <p className="font-heading text-xl font-semibold mb-2">{lang === 'es' ? 'Quiero compartir mi proyecto' : 'I want to share my project'}</p>
                    <p className="text-sm text-white/70">{lang === 'es' ? 'Llegar directo a un dashboard que se sienta como un álbum vivo: perfil, tracks, directos y comunidad.' : 'Go straight into a dashboard that feels like a living album: profile, tracks, live and community.'}</p>
                  </button>
                </div>
              </div>
            )}

            {step === 1 && form.selected_role === 'user' && (
              <div className="space-y-5">
                <h2 className="font-heading text-2xl font-bold">{lang === 'es' ? 'Elige lo que te mueve' : 'Choose what moves you'}</h2>
                <div>
                  <p className="text-sm text-white/80 mb-3">{lang === 'es' ? 'Géneros' : 'Genres'}</p>
                  <div className="flex flex-wrap gap-2">
                    {genreOptions.map((genre) => (
                      <button key={genre} data-testid={`onboarding-genre-${genre}`} onClick={() => toggleItem('favorite_genres', genre)} className={`rounded-full px-4 py-2 text-sm border transition-all duration-200 ${form.favorite_genres.includes(genre) ? 'bg-pulse-primary text-black border-pulse-primary scale-[1.03]' : 'border-white/10 text-white/75'}`}>{genre}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-white/80 mb-3">{lang === 'es' ? 'Moods' : 'Moods'}</p>
                  <div className="flex flex-wrap gap-2">
                    {moodOptions.map((mood) => (
                      <button key={mood} data-testid={`onboarding-mood-${mood}`} onClick={() => toggleItem('favorite_moods', mood)} className={`rounded-full px-4 py-2 text-sm border transition-all duration-200 ${form.favorite_moods.includes(mood) ? 'bg-pulse-primary text-black border-pulse-primary scale-[1.03]' : 'border-white/10 text-white/75'}`}>{mood}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 1 && form.selected_role === 'artist' && (
              <div className="space-y-5">
                <h2 className="font-heading text-2xl font-bold">{lang === 'es' ? 'Cuéntanos sobre tu proyecto' : 'Tell us about your project'}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <input data-testid="onboarding-artist-name" value={form.artist_name} onChange={(e) => setForm((f) => ({ ...f, artist_name: e.target.value }))} placeholder={lang === 'es' ? 'Nombre artístico' : 'Artist name'} className="bg-pulse-surface border border-pulse-border rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input data-testid="onboarding-artist-city" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} placeholder={lang === 'es' ? 'Ciudad' : 'City'} className="bg-pulse-surface border border-pulse-border rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                </div>
                <textarea data-testid="onboarding-artist-story" value={form.artist_story} onChange={(e) => setForm((f) => ({ ...f, artist_story: e.target.value }))} placeholder={lang === 'es' ? 'Describe tu energía, tu estilo y lo que quieres provocar' : 'Describe your energy, style and what you want to provoke'} className="w-full min-h-[130px] bg-pulse-surface border border-pulse-border rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                <div>
                  <p className="text-sm text-white/80 mb-3">{lang === 'es' ? 'Estilos que te representan' : 'Styles that represent you'}</p>
                  <div className="flex flex-wrap gap-2">
                    {genreOptions.map((genre) => (
                      <button key={genre} data-testid={`onboarding-artist-genre-${genre}`} onClick={() => toggleItem('artist_genres', genre)} className={`rounded-full px-4 py-2 text-sm border transition-all duration-200 ${form.artist_genres.includes(genre) ? 'bg-[#FF3B30] text-white border-[#FF3B30] scale-[1.03]' : 'border-white/10 text-white/75'}`}>{genre}</button>
                    ))}
                  </div>
                </div>
                <div className="rounded-[28px] border border-amber-500/25 bg-amber-500/5 p-5 space-y-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-amber-200/90">{lang === 'es' ? 'Perfil fiscal (cuenta)' : 'Tax profile (account)'}</p>
                  <p className="text-xs text-white/65 leading-relaxed">{lang === 'es' ? 'Orientativo para liquidaciones y documentos. No sustituye asesoramiento fiscal.' : 'For payouts and documents. Not tax advice.'}</p>
                  <label className="block text-sm">
                    <span className="text-pulse-muted text-xs">{lang === 'es' ? 'País de residencia fiscal' : 'Tax residence country'} *</span>
                    <select
                      data-testid="onboarding-tax-country"
                      value={form.tax_country}
                      onChange={(e) => setForm((f) => ({ ...f, tax_country: e.target.value }))}
                      className="mt-1 w-full bg-pulse-surface border border-pulse-border rounded-2xl px-4 py-3 text-sm"
                    >
                      <option value="">{lang === 'es' ? 'Selecciona…' : 'Select…'}</option>
                      {Object.keys(taxRates).map((code) => (
                        <option key={code} value={code}>{taxRates[code]?.name || code}</option>
                      ))}
                    </select>
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    <input
                      value={form.tax_id}
                      onChange={(e) => setForm((f) => ({ ...f, tax_id: e.target.value }))}
                      placeholder={lang === 'es' ? 'NIF / VAT / Tax ID (opc.)' : 'Tax ID (opt.)'}
                      className="bg-pulse-surface border border-pulse-border rounded-2xl px-4 py-3 text-sm"
                    />
                    <select
                      value={form.tax_type}
                      onChange={(e) => setForm((f) => ({ ...f, tax_type: e.target.value }))}
                      className="bg-pulse-surface border border-pulse-border rounded-2xl px-4 py-3 text-sm"
                    >
                      <option value="individual">{lang === 'es' ? 'Persona física' : 'Individual'}</option>
                      <option value="company">{lang === 'es' ? 'Empresa / autónomo societario' : 'Company'}</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-white/80">
                    <input type="checkbox" checked={form.vat_registered} onChange={(e) => setForm((f) => ({ ...f, vat_registered: e.target.checked }))} className="accent-pulse-primary" />
                    {lang === 'es' ? 'Estoy registrado a efectos de IVA/VAT cuando aplique' : 'VAT registered where applicable'}
                  </label>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-3">{lang === 'es' ? 'Tu experiencia está lista' : 'Your experience is ready'}</p>
                  <h2 className="font-heading text-2xl font-bold">{lang === 'es' ? 'Así se vive PulseMusic' : 'This is how PulseMusic feels'}</h2>
                </div>
                <div className="rounded-[28px] border border-pulse-primary/20 bg-pulse-primary/5 p-5">
                  <p className="text-sm text-white/80 mb-3">{lang === 'es' ? 'Resumen visual' : 'Visual summary'}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs">{form.selected_role === 'artist' ? '🎵 Artista' : '🎧 Descubrir'}</span>
                    {(onboardingSummary.genres || []).slice(0, 4).map((item) => <span key={item} className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs">{item}</span>)}
                    {(onboardingSummary.moods || []).slice(0, 3).map((item) => <span key={item} className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs">{item}</span>)}
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { icon: Disc3, title: lang === 'es' ? 'Descubrir' : 'Discover', body: lang === 'es' ? 'Encuentra música y artistas que encajan contigo.' : 'Find music and artists that fit you.' },
                    { icon: MapPinned, title: lang === 'es' ? 'Mapa' : 'Map', body: lang === 'es' ? 'Explora qué está pasando ahora en la escena.' : 'Explore what is happening in the scene right now.' },
                    { icon: Radio, title: lang === 'es' ? 'En vivo' : 'Live', body: lang === 'es' ? 'Participa en experiencias con artistas en tiempo real. Juegos, votaciones, retos y más.' : 'Join real-time artist experiences. Games, voting, challenges and more.' },
                  ].map((item) => (
                    <div key={item.title} className="rounded-[24px] border border-white/10 bg-white/[0.03] p-5">
                      <item.icon size={18} className="text-pulse-primary mb-3" />
                      <p className="font-heading text-lg font-semibold mb-2">{item.title}</p>
                      <p className="text-sm text-white/70 leading-relaxed">{item.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <h2 className="font-heading text-2xl font-bold">{lang === 'es' ? 'Antes de continuar' : 'Before you continue'}</h2>
                <div className="rounded-[28px] border border-white/10 bg-white/[0.03] p-5 space-y-4">
                  <label className="flex items-start gap-3 text-sm text-white/80">
                    <input data-testid="onboarding-accept-terms" type="checkbox" checked={form.accept_terms} onChange={(e) => setForm((f) => ({ ...f, accept_terms: e.target.checked }))} className="mt-1" />
                    <span>
                      {lang === 'es' ? 'Acepto los ' : 'I accept the '}
                      <button type="button" className="text-pulse-primary underline" onClick={() => openLegalModal('terms')}>{lang === 'es' ? 'Términos y Condiciones' : 'Terms and Conditions'}</button>
                      {lang === 'es' ? ' de PulseMusic.' : ' of PulseMusic.'}
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-white/80">
                    <input data-testid="onboarding-accept-privacy" type="checkbox" checked={form.accept_privacy} onChange={(e) => setForm((f) => ({ ...f, accept_privacy: e.target.checked }))} className="mt-1" />
                    <span>
                      {lang === 'es' ? 'Acepto la ' : 'I accept the '}
                      <button type="button" className="text-pulse-primary underline" onClick={() => openLegalModal('privacy')}>{lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}</button>
                      {lang === 'es' ? ' y el tratamiento de mis datos para usar la plataforma.' : ' and the processing of my data to use the platform.'}
                    </span>
                  </label>
                  <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-sm text-white/70 leading-relaxed">
                    <p className="mb-2">{lang === 'es' ? 'Base legal preparada para una plataforma musical global:' : 'Legal base prepared for a global music platform:'}</p>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className="text-pulse-primary underline" onClick={() => openLegalModal('copyright')}>{lang === 'es' ? 'copyright y moderación' : 'copyright and moderation'}</button>
                      {form.selected_role === 'artist' && <button type="button" className="text-pulse-primary underline" onClick={() => openLegalModal('artist')}>{lang === 'es' ? 'condiciones de artista' : 'artist terms'}</button>}
                    </div>
                  </div>
                  {form.selected_role === 'artist' && (
                    <label className="flex items-start gap-3 text-sm text-white/80">
                      <input data-testid="onboarding-accept-artist-terms" type="checkbox" checked={form.accept_artist_terms} onChange={(e) => setForm((f) => ({ ...f, accept_artist_terms: e.target.checked }))} className="mt-1" />
                      <span>{lang === 'es' ? 'Confirmo que tengo derechos suficientes sobre lo que suba o comparta y que respeto el trabajo de otros creadores.' : 'I confirm I have sufficient rights over what I upload or share and that I respect the work of other creators.'}</span>
                    </label>
                  )}
                </div>
              </div>
            )}

            {error && <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>}

            <div className="flex items-center justify-between gap-3">
              <button data-testid="onboarding-back-button" disabled={step === 0 || loading} onClick={() => setStep((current) => Math.max(0, current - 1))} className="rounded-full border border-white/10 px-5 py-3 text-sm text-white/70 disabled:opacity-40">
                {lang === 'es' ? 'Atrás' : 'Back'}
              </button>
              {step < 3 ? (
                <button data-testid="onboarding-next-button" onClick={handleNext} className="rounded-full bg-pulse-primary px-6 py-3 text-sm font-bold text-black inline-flex items-center gap-2">
                  {lang === 'es' ? 'Seguir' : 'Continue'} <ArrowRight size={16} />
                </button>
              ) : (
                <button data-testid="onboarding-complete-button" disabled={loading} onClick={handleSubmit} className="rounded-full bg-pulse-primary px-6 py-3 text-sm font-bold text-black inline-flex items-center gap-2 disabled:opacity-50">
                  <Sparkles size={16} /> {loading ? (lang === 'es' ? 'Preparando...' : 'Preparing...') : (lang === 'es' ? 'Empezar a explorar' : 'Start exploring')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}